import { Backdrop, debounce } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format, isValid } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import LoadingSpinner from "../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { isoFormat } from "../../../../common/helpers/formatDateTime";
import getLocale from "../../../../common/helpers/getLocale";
import { EventPayload } from "../../../../common/types/Event";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";
import useBackendPolling from "../../../../hooks/useBackendPolling";
import useIsLoading from "../../../../hooks/useIsLoading";
import {
  LocalStorageAttachment,
  searchCertificates,
} from "../../../talents/talentAPI";
import {
  addTalentCertificate,
  approveTalentCertificate,
  fetchTalentCertificateDocuments,
  getAttachmentsFromLocalStorage,
  getCertificateDocumentUploadUrl,
  rejectTalentCertificate,
  removeTalentCertificateDocument,
  selectTalentById,
  updateAttachmentsInLocalStorage,
  updateTalentCertificate,
  uploadCertificate,
} from "../../../talents/talentsSlice";
import {
  AddTalentCertificate,
  ApproveTalentCertificate,
  RejectTalentCertificate,
  RemoveTalentCertificateDocument,
  UpdateTalentCertificate,
} from "../../../talents/types/commands";
import {
  TalentCertificateAdded,
  TalentCertificateUpdated,
} from "../../../talents/types/events";
import { EditMode } from "../../TalentDetails";
import {
  DRIVING_LICENSES_CERT_TYPES,
  getTalentContentKey,
  LOCAL_STORAGE_KEY,
  PASSPORT_CERT_TYPE,
  TalentContentKey,
} from "../../constants";
import WorkPermitForm from "./WorkPermitForm";
import { CertificateItem, FormValues } from "./types";

type Props = {
  title: string;
  editMode?: EditMode;
  drivingLicenseMode?: boolean;
  onClose: () => void;
  fixedType?: string;
};

const WorkPermitFormContainer = ({
  title,
  editMode,
  drivingLicenseMode = false,
  onClose,
  fixedType = undefined,
}: Props) => {
  const [certificateList, setCertificateList] = useState<CertificateItem[]>([]);
  const [certSearchTerm, setCertSearchTerm] = useState("");
  const [removableDocuments, setRemovableDocuments] = useState<
    { filename: string; id: string; version: number }[] | []
  >([]);

  const newCertificateRef = useRef({ id: "", version: 0 });

  const { t } = useTranslation("talentProfile");
  const { talentId } = useParams();
  const locale = getLocale();
  const dispatch = useAppDispatch();

  const talent = useAppSelector((state) => selectTalentById(state, talentId!));

  const user = useAppSelector((state) => state.user.user);

  const existingCertificateTypes =
    talent?.certificates?.map((cert) => cert.type) || [];

  const { isPolling, startPolling, stopPolling, isTimeout, error } =
    useBackendPolling({
      callback: async () => shouldContinuePolling(),
    });

  useEffect(() => {
    if (isTimeout || error) {
      stopPolling();
    }
    return () => stopPolling();
    // eslint-disable-next-line
  }, [isTimeout]);

  const isLoading = useIsLoading(["talents"]);
  const translations = useAppSelector((state) => state.localized);

  const certificate = talent?.certificates?.find(
    (cert) => cert.id === editMode?.id,
  );

  const shouldContinuePolling = async () => {
    const attachmentsInStorage: LocalStorageAttachment[] = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEY) || "{}",
    ).filter(
      (attachment: any) =>
        attachment.path === "talents" &&
        attachment.pathId[0] === talentId &&
        attachment.pathId[1] !== "cv" &&
        attachment.status === "scanning",
    );
    let isCompleted = false;
    // Fetch data from backend. This overwrites state, so we need to
    // check for documents in scan from local storage.
    dispatch(fetchTalentCertificateDocuments({ talentId }))
      .then((action) => {
        if (isFulfilled(action)) {
          dispatch(updateAttachmentsInLocalStorage(talentId as string));
          const allCertDocumentNames = action.payload.certificates?.flatMap(
            (cert) => cert.documents?.map((doc) => doc.filename),
          );
          isCompleted = attachmentsInStorage.every((attachment) =>
            allCertDocumentNames?.includes(attachment.filename),
          );
          dispatch(getAttachmentsFromLocalStorage(talentId as string));
        }
        isCompleted && stopPolling();
      })
      .catch((err) => {
        console.error("Error fetching talent certificate documents", err);
        stopPolling();
      });
  };

  useEffect(() => {
    // Check if needs polling when modal is opened
    dispatch(getAttachmentsFromLocalStorage(talentId as string));
    if (
      !!editMode?.enabled &&
      (editMode.content ===
        getTalentContentKey(TalentContentKey.WorkPermits, "edit") ||
        editMode.content ===
          getTalentContentKey(TalentContentKey.DrivingLicenses, "edit"))
    ) {
      // If there is any work permit document in redux store with status "scanning", start polling
      !isPolling &&
        talent.certificates?.some((cert) => {
          return cert.documents?.some((doc) => {
            if (doc.status === "scanning") return true;
            return false;
          });
        }) &&
        startPolling();
    }
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, dispatch, talentId]);

  const certificateType = fixedType || (certificate && certificate.type);
  const translatedCertificateName = certificateType
    ? translations.certificate[certificateType]
    : "";
  const certificateRequiresDocument = fixedType
    ? true
    : certificate && certificate.requiresDocument;

  const autocompleteDefaultValue = useMemo(() => {
    return !editMode
      ? fixedType
        ? {
            id: fixedType,
            name: translatedCertificateName,
            requiresDocument: certificateRequiresDocument || false,
          }
        : null
      : {
          id: certificateType || "",
          name: translatedCertificateName || "",
          requiresDocument: certificateRequiresDocument || false,
        };
  }, [
    editMode,
    fixedType,
    translatedCertificateName,
    certificateRequiresDocument,
    certificateType,
  ]);

  const validToDefaultValue = useMemo(() => {
    return certificate?.validTo ? new Date(certificate?.validTo) : null;
  }, [certificate]);

  const validUFNDefaultValue = editMode
    ? certificate?.validTo
      ? isValid(format(new Date(certificate?.validTo), "dd.MM.yyyy"))
      : true
    : true;

  const defaultValues = useMemo(() => {
    return {
      type: autocompleteDefaultValue,
      validTo: validToDefaultValue,
      validUntilFurtherNotice: validUFNDefaultValue,
      approved: !!certificate?.approvedAt,
      rejected: !!certificate?.rejectedAt,
      documents: certificate?.documents || [],
    };
  }, [
    certificate,
    autocompleteDefaultValue,
    validToDefaultValue,
    validUFNDefaultValue,
  ]);
  // const immutableDefaults = JSON.parse(JSON.stringify(defaultValues)) as FormValues;
  // console.log("defaultValues", defaultValues);
  const methods = useForm({
    mode: "onChange",
    defaultValues,
  });

  // useEffect(() => {
  //   console.log("Default values as passed:", JSON.stringify(defaultValues));
  // }, [defaultValues]);

  // const name = methods.getValues("type")?.name;
  // console.log("methods", methods, name);
  const approvedChanged = Object.keys(methods.formState.dirtyFields).includes(
    "approved",
  );
  const rejectedChanged = Object.keys(methods.formState.dirtyFields).includes(
    "rejected",
  );

  const onInputChange = debounce(
    (term: string) => setCertSearchTerm(term),
    400,
  );

  useEffect(() => {
    searchCertificates({
      ...(certSearchTerm && { term: certSearchTerm }),
      language: locale.code || "fi",
    }).then((res) => {
      if (res.data) {
        const translatedCerticates = res.data.reduce(
          (acc: CertificateItem[], curr) => {
            if (
              translations.certificate[curr.id] && drivingLicenseMode
                ? DRIVING_LICENSES_CERT_TYPES.includes(curr.id)
                : !DRIVING_LICENSES_CERT_TYPES.includes(curr.id)
            ) {
              if (!fixedType && curr.id === PASSPORT_CERT_TYPE) {
                // Skip passport if not in fixedType
              } else {
                acc.push({
                  id: curr.id,
                  name: translations.certificate[curr.id],
                  requiresDocument: curr.requiresDocument,
                });
              }
            }
            return acc;
          },
          [],
        );
        // console.log("setting cert list", translatedCerticates);
        setCertificateList(translatedCerticates);
      }
    });
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    certSearchTerm,
    locale.code,
    translations.certificate,
    drivingLicenseMode,
  ]);

  const onCancel = () => {
    methods.reset(defaultValues);
    onClose();
  };

  const onSave = async (data: FormValues) => {
    const validTo = data.validTo ? format(data.validTo, isoFormat) : undefined;

    const certificateParams = {
      talentId: talentId as string,
      type: data.type?.id as string,
      requiresDocument: data.type?.requiresDocument as boolean,
      validTo,
    };

    const documentsChanged = Object.keys(
      methods.formState.dirtyFields,
    ).includes("documents");

    const dispatchUploadDocument = (
      url: string,
      content: File,
    ): Promise<boolean> =>
      dispatch(
        uploadCertificate({
          url,
          content,
        }),
      ).then((action) => isFulfilled(action));

    const dispatchUpdateTalentCertificate = async (
      cmdPayload: UpdateTalentCertificate,
    ): Promise<EventPayload<TalentCertificateUpdated>> =>
      dispatch(updateTalentCertificate(cmdPayload))
        .unwrap()
        .then((eventPayload) => {
          onClose();
          return Promise.resolve(eventPayload);
        })
        .catch(() => Promise.reject());

    const dispatchAddTalentCertificate = async (
      cmdPayload: AddTalentCertificate,
    ): Promise<EventPayload<TalentCertificateAdded>[]> => {
      return dispatch(addTalentCertificate(cmdPayload))
        .unwrap()
        .then((eventPayload) => Promise.resolve(eventPayload))
        .catch(() => Promise.reject());
    };

    const dispatchUploadCertificateDocument = async (
      document: File,
      id: string,
    ) =>
      dispatch(
        getCertificateDocumentUploadUrl({
          certificateId: id,
          talentId: talentId as string,
          filename: document.name,
          size: document.size.toString(),
        }),
      )
        .unwrap()
        .then((res) => {
          if (res) {
            if (!editMode) {
              newCertificateRef.current = {
                ...newCertificateRef.current,
                version: newCertificateRef.current.version + 1,
              };
            }
            // console.log("dispatchUploadCertificateDocument", res.url, document);
            return dispatchUploadDocument(res.url, document);
          }
        })
        .catch((err) => {
          // console.log("dispatchUploadCertificateDocument error", err);
          return Promise.reject(err);
        });

    const dispatchRemoveCertificateDocument = async (
      cmdPayload: RemoveTalentCertificateDocument,
    ) =>
      await dispatch(removeTalentCertificateDocument(cmdPayload)).then(
        (action) => isFulfilled(action),
      );

    if (editMode) {
      const certificateId = certificate?.id as string;
      let currentVersion = certificate?.version as number;

      const validTo = data.validTo
        ? format(data.validTo, isoFormat)
        : undefined;

      const updatePayload = {
        ...certificateParams,
        id: certificateId,
        version: currentVersion,
        validTo,
      };

      const editResult = await dispatchUpdateTalentCertificate(updatePayload);

      if (removableDocuments) {
        await Promise.all(
          removableDocuments.map(async (document, index) => {
            const versionUpdated = {
              ...document,
              version: editResult?.version || document.version + index,
            };
            return dispatchRemoveCertificateDocument(versionUpdated).then(() =>
              onClose(),
            );
          }),
        );
      }

      if (documentsChanged) {
        // console.log("documentsChanged", data.documents);
        await Promise.all(
          data.documents.map(async (document) => {
            if (document instanceof File)
              return dispatchUploadCertificateDocument(
                document,
                editMode.id,
              ).then(() => onClose());
          }),
        );
      }

      if (approvedChanged && !documentsChanged) {
        const cmdPayload: ApproveTalentCertificate = {
          id: certificateId,
          version: editResult?.version || currentVersion,
          user: user,
        };

        dispatch(approveTalentCertificate(cmdPayload)).then(
          (action) => isFulfilled(action) && onClose(),
        );
      }

      if (rejectedChanged && !documentsChanged) {
        const cmdPayload: RejectTalentCertificate = {
          id: editResult?.id || (certificate?.id as string),
          version: editResult?.version || currentVersion,
          user: user,
        };

        dispatch(rejectTalentCertificate(cmdPayload)).then(
          (action) => isFulfilled(action) && onClose(),
        );
      }
    } else {
      const params = {
        ...certificateParams,
        grantedBy: `${user?.firstName} ${user?.lastName}`,
      };
      const addResult = await dispatchAddTalentCertificate(params);
      const certificateId = addResult[0].id;
      const highestVersion = addResult.reduce(
        (max, event) => Math.max(max, event.version),
        0,
      );
      newCertificateRef.current = {
        id: certificateId,
        version: highestVersion,
      };

      var needClose = true;
      if (documentsChanged) {
        data.documents.map(async (document) => {
          if (document instanceof File) {
            needClose = false;
            return dispatchUploadCertificateDocument(
              document,
              certificateId,
            ).then(() => onClose());
          }
        });
      }

      if (approvedChanged) {
        const cmdPayload: ApproveTalentCertificate = {
          id: certificateId,
          version: highestVersion,
          user: user,
        };

        needClose = false;
        dispatch(approveTalentCertificate(cmdPayload)).then(
          (action) => isFulfilled(action) && onClose(),
        );
      }

      if (rejectedChanged) {
        const cmdPayload: RejectTalentCertificate = {
          id: certificateId,
          version: highestVersion,
          user: user,
        };

        needClose = false;
        dispatch(rejectTalentCertificate(cmdPayload)).then(
          (action) => isFulfilled(action) && onClose(),
        );
      }
      dispatch(getAttachmentsFromLocalStorage(talentId as string));

      if (needClose) {
        onClose();
      }
    }
  };

  const handleRemoveExistingDocuments = (
    filename: string,
    id: string,
    version: number,
  ) => {
    setRemovableDocuments([...removableDocuments, { filename, id, version }]);
  };

  const certificatesThatRequireDocument = [PASSPORT_CERT_TYPE];

  // Check if any documents are uploaded
  const documentUploaded = methods.getValues("documents")?.length > 0;

  const id = methods?.getValues("type")?.id;
  const errors = !!Object.keys(methods.formState.errors).length;
  const keys = !Object.keys(methods.formState.dirtyFields).length;
  const docNeeded = certificatesThatRequireDocument.includes(id || "");
  const saveDisabled =
    errors ||
    keys ||
    // If certificate is type that requires document and it's being approved, check if document is uploaded
    (docNeeded && !documentUploaded);
    
  return (
    <DialogContentWrapper
      title={title}
      margin
      onCancel={onCancel}
      onSave={methods.handleSubmit(onSave)}
      saveDisabled={saveDisabled || isLoading}
    >
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <Backdrop
            open={isLoading}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.4)",
              zIndex: 1000,
            }}
          >
            <LoadingSpinner />
          </Backdrop>
          <WorkPermitForm
            certificateTypeTitle={
              drivingLicenseMode
                ? t("talentProfile:drivingLicenseType")
                : t("talentProfile:certificateType")
            }
            certificate={certificate}
            certificateList={certificateList}
            existingCertificateTypes={existingCertificateTypes}
            onInputChange={onInputChange}
            handleRemoveExistingDocuments={handleRemoveExistingDocuments}
            saveDisabled={saveDisabled || isLoading}
            isEditMode={editMode?.enabled}
            isPolling={isPolling}
          />
        </UnsavedChangesProvider>
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default WorkPermitFormContainer;
